import { useEffect, useState } from "react";
import { Save, Trash2, ArrowUpRight, ArrowUp, ArrowDown, X } from "lucide-react";
import adminApi, { errorText } from "@/adminApi";

const mediaSrc = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s}` : s;
};

function PhotoThumb({ url, testId }) {
  const [broken, setBroken] = useState(false);
  const src = mediaSrc(url);

  useEffect(() => setBroken(false), [url]);

  if (!src || broken) {
    return (
      <div className="media-thumb placeholder" data-testid={testId || "photo-thumb-placeholder"}>
        Preview unavailable
      </div>
    );
  }
  return (
    <img
      className="media-thumb"
      src={src}
      alt="Current homepage photo"
      onError={() => setBroken(true)}
      data-testid={testId}
    />
  );
}

function PhotoCard({ item, slotNumber, isFirst, isLast, onSave, onMove, onDelete, busy }) {
  const [draft, setDraft] = useState(item);
  const [dirty, setDirty] = useState(false);
  const [driveUrl, setDriveUrl] = useState("");
  const [preview, setPreview] = useState(null); // { driveId, previewUrl }
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");

  useEffect(() => {
    setDraft(item);
    setDirty(false);
  }, [item]);

  const set = (k, v) => {
    setDraft((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const importPhoto = async () => {
    if (!driveUrl.trim()) return;
    setImporting(true);
    setImportError("");
    try {
      const { data } = await adminApi.post("/admin/media/replace-preview", { drive_url: driveUrl.trim() });
      setPreview({ driveId: data.drive_id, previewUrl: data.preview_url });
    } catch (e) {
      setImportError(errorText(e));
      setPreview(null);
    } finally {
      setImporting(false);
    }
  };

  const cancelReplacement = () => {
    setPreview(null);
    setDriveUrl("");
    setImportError("");
  };

  const saveAndPublish = async () => {
    const payload = {
      title: draft.title ?? "",
      caption: draft.caption ?? "",
      category: draft.category ?? "",
      alt: draft.alt ?? "",
      order: draft.order ?? 0,
      published: draft.published !== false,
      homepage: !!draft.homepage,
    };
    try {
      await onSave(item.id, payload, preview ? driveUrl.trim() : null);
      setDirty(false);
      setPreview(null);
      setDriveUrl("");
      setImportError("");
    } catch (_) {
      /* handled by parent */
    }
  };

  return (
    <article className="photo-card" data-testid={`photo-card-${item.id}`}>
      <div className="photo-card-top">
        <PhotoThumb url={item.url} testId={`photo-thumb-${item.id}`} />
        <div className="photo-card-meta">
          <p className="eyebrow" data-testid={`photo-slot-label-${item.id}`}>
            PHOTO {String(slotNumber).padStart(2, "0")}
          </p>
          <strong>{item.title || "Untitled photo"}</strong>
          <div className="photo-status-badges">
            <span className="status-pill">{item.category || "Uncategorised"}</span>
            <span className={`status-pill ${item.published !== false ? "on" : "off"}`}>
              {item.published !== false ? "PUBLISHED" : "HIDDEN"}
            </span>
            <span className={`status-pill ${item.homepage ? "on" : "off"}`}>
              {item.homepage ? "ON HOMEPAGE" : "OFF HOMEPAGE"}
            </span>
            <span className="status-pill">ORDER {item.order ?? 0}</span>
          </div>
          <div className="reorder-btns">
            <button
              type="button"
              disabled={isFirst || busy}
              onClick={() => onMove(item, -1)}
              data-testid={`photo-move-up-${item.id}`}
            >
              <ArrowUp size={12} /> MOVE UP
            </button>
            <button
              type="button"
              disabled={isLast || busy}
              onClick={() => onMove(item, 1)}
              data-testid={`photo-move-down-${item.id}`}
            >
              <ArrowDown size={12} /> MOVE DOWN
            </button>
          </div>
        </div>
      </div>

      <div className="editor-fields">
        <label>
          TITLE
          <input value={draft.title ?? ""} onChange={(e) => set("title", e.target.value)} data-testid={`photo-title-${item.id}`} />
        </label>
        <label>
          CATEGORY
          <input value={draft.category ?? ""} onChange={(e) => set("category", e.target.value)} data-testid={`photo-category-${item.id}`} />
        </label>
        <label>
          ORDER
          <input
            type="number"
            value={draft.order ?? 0}
            onChange={(e) => set("order", e.target.value === "" ? "" : Number(e.target.value))}
            data-testid={`photo-order-${item.id}`}
          />
        </label>
        <label className="editor-long">
          CAPTION
          <textarea value={draft.caption ?? ""} onChange={(e) => set("caption", e.target.value)} data-testid={`photo-caption-${item.id}`} />
        </label>
        <label>
          ALT TEXT
          <input value={draft.alt ?? ""} onChange={(e) => set("alt", e.target.value)} data-testid={`photo-alt-${item.id}`} />
        </label>
        <label className="editor-toggle">
          <input
            type="checkbox"
            checked={draft.published !== false}
            onChange={(e) => set("published", e.target.checked)}
            data-testid={`photo-published-${item.id}`}
          />
          PUBLISHED
        </label>
        <label className="editor-toggle">
          <input
            type="checkbox"
            checked={!!draft.homepage}
            onChange={(e) => set("homepage", e.target.checked)}
            data-testid={`photo-homepage-${item.id}`}
          />
          SHOW ON HOMEPAGE
        </label>
      </div>

      <div className="replace-panel">
        <p className="eyebrow">REPLACE PHOTO FROM GOOGLE DRIVE</p>
        <div className="replace-input-row">
          <input
            value={driveUrl}
            placeholder="Paste Google Drive photo sharing link"
            onChange={(e) => setDriveUrl(e.target.value)}
            data-testid={`photo-drive-input-${item.id}`}
          />
          <button
            type="button"
            className="gold-btn"
            disabled={importing || !driveUrl.trim()}
            onClick={importPhoto}
            data-testid={`photo-import-${item.id}`}
          >
            {importing ? "IMPORTING…" : "IMPORT"} <ArrowUpRight size={14} />
          </button>
        </div>
        {importError && (
          <p className="notice import-error" data-testid={`photo-import-error-${item.id}`}>
            {importError}
          </p>
        )}
        {preview && (
          <div className="replace-preview-compare" data-testid={`photo-compare-${item.id}`}>
            <div className="compare-col">
              <p className="eyebrow">CURRENT PHOTO</p>
              <PhotoThumb url={item.url} testId={`photo-current-${item.id}`} />
            </div>
            <div className="compare-col">
              <p className="eyebrow">NEW PHOTO</p>
              <PhotoThumb url={preview.previewUrl} testId={`photo-new-preview-${item.id}`} />
            </div>
          </div>
        )}
      </div>

      <div className="editor-actions">
        <button
          className="gold-btn"
          disabled={busy || (!dirty && !preview)}
          onClick={saveAndPublish}
          data-testid={`photo-save-${item.id}`}
        >
          <Save size={14} /> SAVE & PUBLISH
        </button>
        {preview && (
          <button className="delete-btn" onClick={cancelReplacement} data-testid={`photo-cancel-${item.id}`}>
            <X size={14} /> CANCEL REPLACEMENT
          </button>
        )}
        <button className="delete-btn" disabled={busy} onClick={() => onDelete(item.id)} data-testid={`photo-delete-${item.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}

export default function HomepagePhotosManager({ items, refresh, setMessage }) {
  const [busy, setBusy] = useState(false);
  const sorted = [...(items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const save = async (id, fields, driveUrl) => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/media/${id}/replace`, { drive_url: driveUrl || null, data: fields });
      setMessage(driveUrl ? "New photo saved & published. The homepage now uses this image." : "Saved & published.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const move = async (item, direction) => {
    const idx = sorted.findIndex((x) => x.id === item.id);
    const swapWith = sorted[idx + direction];
    if (!swapWith) return;
    setBusy(true);
    try {
      await adminApi.put(`/admin/media/${item.id}/replace`, { data: { order: swapWith.order ?? 0 } });
      await adminApi.put(`/admin/media/${swapWith.id}/replace`, { data: { order: item.order ?? 0 } });
      setMessage("Order updated.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this homepage photo slot permanently?")) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/content/media/${id}`);
      setMessage("Photo slot removed.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const addSlot = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/content/media", {
        data: { title: "New photo", published: false, homepage: false, order: sorted.length },
      });
      setMessage("New photo slot added — paste a Google Drive link and Save & Publish.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="photos-manager">
      <div className="photos-manager-head">
        <p className="muted">
          Each card below is one curated homepage photograph. Find the one you want to change by its
          thumbnail, paste a new Google Drive link, click IMPORT to preview it, then SAVE &amp; PUBLISH
          to replace it. The old photo stays live until you publish.
        </p>
        <button className="gold-btn" disabled={busy} onClick={addSlot} data-testid="photo-add-slot">
          ADD PHOTO SLOT <ArrowUpRight size={15} />
        </button>
      </div>
      <div className="editor-list photo-list">
        {sorted.map((item, i) => (
          <PhotoCard
            key={item.id}
            item={item}
            slotNumber={i + 1}
            isFirst={i === 0}
            isLast={i === sorted.length - 1}
            onSave={save}
            onMove={move}
            onDelete={remove}
            busy={busy}
          />
        ))}
        {sorted.length === 0 && <p className="muted">No homepage photos yet — use ADD PHOTO SLOT above.</p>}
      </div>
    </div>
  );
}
